import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  OutlinedInput,
  CircularProgress
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { permissionsAPI } from '../../shared/services/api';
import toast from 'react-hot-toast';

const PermissionModuleDialog = ({ open, onClose, module = null, isEdit = false }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    portalAccess: []
  });

  const portals = ['member', 'corporate', 'provider', 'intermediary', 'insurer', 'irda', 'admin'];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: permissionsAPI.createPermission,
    onSuccess: () => {
      queryClient.invalidateQueries(['permissions']);
      toast.success('Permission module created successfully');
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create permission module');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => permissionsAPI.updatePermission(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['permissions']);
      toast.success('Permission module updated successfully');
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update permission module');
    },
  });

  // Load module data when editing
  useEffect(() => {
    if (isEdit && module) {
      setFormData({
        name: module.name || '',
        displayName: module.displayName || '',
        portalAccess: module.portalAccess || []
      });
    } else {
      setFormData({
        name: '',
        displayName: '',
        portalAccess: []
      });
    }
  }, [isEdit, module, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (isEdit && module) {
      updateMutation.mutate({ id: module._id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handlePortalAccessChange = (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      portalAccess: typeof value === 'string' ? value.split(',') : value
    });
  };

  const handleClose = () => {
    if (!createMutation.isPending && !updateMutation.isPending) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {isEdit ? 'Edit Permission Module' : 'Create Permission Module'}
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Module Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
              helperText="Unique identifier for the module (lowercase, no spaces)"
            />
            
            <TextField
              label="Display Name"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              required
              fullWidth
              helperText="Human-readable name shown in the UI"
            />
            

            <FormControl fullWidth>
              <InputLabel>Portal Access</InputLabel>
              <Select
                multiple
                value={formData.portalAccess}
                onChange={handlePortalAccessChange}
                input={<OutlinedInput label="Portal Access" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={value.charAt(0).toUpperCase() + value.slice(1)}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                {portals.map((portal) => (
                  <MenuItem key={portal} value={portal}>
                    {portal.charAt(0).toUpperCase() + portal.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleClose}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {(createMutation.isPending || updateMutation.isPending) ? (
              <CircularProgress size={20} />
            ) : (
              isEdit ? 'Update' : 'Create'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PermissionModuleDialog;
